'use client';

import Button from '@/components/ui/Button';

interface BlogPaginationProps {
  currentPage: number;
  totalPages: number;
  query?: Record<string, string | undefined>;
}

export function BlogPagination({ currentPage, totalPages, query = {} }: BlogPaginationProps) {
  if (totalPages <= 1) return null;

  const buildUrl = (page: number) => {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.set(key, value);
      }
    });
    params.set('page', String(page));
    return `/blog?${params.toString()}`;
  };

  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
  const visiblePages = pages.filter((page) => {
    if (page === 1 || page === totalPages) return true;
    return Math.abs(page - currentPage) <= 1;
  });

  const items: (number | 'ellipsis')[] = [];
  visiblePages.forEach((page, index) => {
    if (index > 0 && page - visiblePages[index - 1] > 1) {
      items.push('ellipsis');
    }
    items.push(page);
  });

  const disabledClass = 'inline-flex min-h-11 items-center justify-center rounded-lg border border-border bg-secondary px-4 text-sm font-medium text-secondary-foreground opacity-40';

  return (
    <nav aria-label="Blog pagination" className="flex items-center justify-center gap-2">
      {currentPage > 1 ? (
        <Button href={buildUrl(currentPage - 1)} variant="secondary">
          Previous
        </Button>
      ) : (
        <span className={disabledClass}>Previous</span>
      )}

      <div className="flex items-center gap-1">
        {items.map((item, index) =>
          item === 'ellipsis' ? (
            <span key={`ellipsis-${index}`} className="px-2 text-ash">
              …
            </span>
          ) : item === currentPage ? (
            <span
              key={item}
              className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground"
              aria-current="page"
            >
              {item}
            </span>
          ) : (
            <Button key={item} href={buildUrl(item)} variant="secondary" className="min-w-11">
              {item}
            </Button>
          )
        )}
      </div>

      {currentPage < totalPages ? (
        <Button href={buildUrl(currentPage + 1)} variant="secondary">
          Next
        </Button>
      ) : (
        <span className={disabledClass}>Next</span>
      )}
    </nav>
  );
}

export default BlogPagination;
